import { useMutation } from '@graphcommerce/graphql'
import { ProofInterface } from '@graphcommerce/graphql-mesh'
import { i18n } from '@lingui/core'
import { Modal, Switch, Tooltip } from '@mui/material'
import { Dispatch, SetStateAction, useState } from 'react'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Document, Page, pdfjs } from 'react-pdf'
import useWindowSize from '../../hooks/useWindowSize'
import { ApproveButton, TertiaryButton, DeclineButton } from '../Button/ButtonStyles'
import { SlideinModalHeader } from '../Layout/SlideinModalHeader'
import { AlertPrompt } from '../Prompt/AlertPrompt'
import { ErrorPrompt } from '../Prompt/ErrorPrompt'
import { TextInput } from '../TextInput'
import { ApproveProofDocument } from './ApproveProof.gql'
import { ChatBox } from './ChatBox'
import { DeclineProofDocument } from './DeclineProof.gql'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

type ReviewProofModalProps = {
  proofs: ProofInterface[]
  isDisplayed: boolean
  setIsDisplayed: Dispatch<SetStateAction<boolean>>
  children?: React.ReactNode
  isChatHistoryShown?: boolean
}

export function ReviewProofModal(props: ReviewProofModalProps) {
  const { proofs, isDisplayed, setIsDisplayed, isChatHistoryShown = true, children } = props
  const [isApproving, setIsApproving] = useState<boolean>(false)
  const [isRejecting, setIsRejecting] = useState<boolean>(false)
  const [rejectComments, setRejectComments] = useState<string>()
  const [approverFirstName, setApproverFirstName] = useState<string>()
  const [approverLastName, setApproverLastName] = useState<string>()
  const [showChatHistory, setShowChatHistory] = useState<boolean>(isChatHistoryShown)
  const [error, setError] = useState<Error>()
  const [displayPrompt, setDisplayPrompt] = useState<boolean>(false)
  const [promptTitle, setPromptTitle] = useState<string>('Approve Proof')
  const [promptMessage, setPromptMessage] = useState<string>('Thank you for your approval!')
  const [isDeclining, setIsDeclining] = useState<boolean>(false)

  const [toggleLabel, setAToggleLabel] = useState<string>(i18n._('Hide comments'))
  let status = proofs[proofs.length - 1]?.status ?? i18n._('Initial Mockup')
  status = status.toUpperCase() === 'PENDING APPROVAL' ? 'Awaiting Approval' : status
  const proofFileExt = proofs[proofs.length - 1]?.file_url?.split('.')?.pop()
  const isPDFProofFile = proofFileExt?.toUpperCase() === 'PDF'
  const { isMobile } = useWindowSize()

  const handleClose = () => setIsDisplayed(false)

  const handleDownloadLatestProof = () => {
    if (proofs && proofs?.length > 0) {
      const link = document.createElement('a')
      // eslint-disable-next-line no-unsafe-optional-chaining
      link.href = proofs[proofs?.length - 1].file_url ?? ''
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const isStatusApproved = status.toUpperCase() === 'APPROVED'
  const isDenied = status.toUpperCase() === 'DENIED'

  const [approveProof] = useMutation(ApproveProofDocument)
  const [declineProof] = useMutation(DeclineProofDocument)

  const resetValues = () => {
    setIsApproving(false)
    setIsRejecting(false)
    setRejectComments('')
    setApproverFirstName('')
    setApproverLastName('')
    setShowChatHistory(false)
    setDisplayPrompt(false)
    setPromptTitle('')
    setPromptMessage('')
  }

  const handleCloseAndResetValues = () => {
    resetValues()
    setIsDisplayed(false)
  }

  const handleApproveProof = async (author: string) => {
    try {
      setPromptTitle('Approve Proof')
      setPromptMessage('Thank you for your approval! Your order has been submitted for production.')
      setDisplayPrompt(true)
      await approveProof({
        variables: {
          proofId: proofs[proofs.length - 1].id,
          comments: i18n._('Approved by: {0}', { 0: author }),
        },
      })
    } catch (e: any) {
      setError(e as Error)
    }
  }

  const handleDeclineProof = async (comments: string | undefined) => {
    try {
      setPromptTitle('Reject Proof')
      setPromptMessage(
        'Thank you for your feedback! We will now begin working on your revised proof.',
      )
      setDisplayPrompt(true)
      await declineProof({
        variables: { proofId: proofs[proofs.length - 1]?.id, comments: comments ?? '' },
      })
    } catch (e: any) {
      setError(e as Error)
    }
  }

  const handleToggleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    event.target.checked
      ? setAToggleLabel(i18n._('Hide comments'))
      : setAToggleLabel(i18n._('Show comments'))
    setShowChatHistory(event.target.checked)
  }

  const proofTitle =
    // eslint-disable-next-line no-nested-ternary
    !isRejecting && !isApproving
      ? status.toUpperCase() === 'APPROVED'
        ? i18n._('Appproved Product Art')
        : status
      : isDenied
      ? 'New Proof Pending'
      : ''
  return (
    <Modal className='z-10 ' open={isDisplayed} onClose={handleCloseAndResetValues}>
      <div className='absolute left-[5vw] top-[5vh] w-[90vw] overflow-auto rounded-md border-solid bg-pure-white lg:left-[15vw] lg:w-[70vw]'>
        <div>
          <SlideinModalHeader title={proofTitle} onClose={handleClose} withHorizontalMargin />
          <div className='mx-5 mb-5 flex gap-x-[80px]'>
            {!isApproving && !isRejecting && !isStatusApproved && (
              <div className='flex items-center justify-end pr-5 lg:hidden'>
                <Tooltip title={toggleLabel}>
                  <div className='flex flex-col items-center'>
                    <Switch checked={showChatHistory} onChange={handleToggleSwitch} size='medium' />
                    <span className='Type-Small-Regular text-center text-black'>{toggleLabel}</span>
                  </div>
                </Tooltip>
              </div>
            )}
            <div className='mr-10 hidden items-center justify-end gap-x-5 lg:flex'>
              {!isApproving && !isRejecting && !isStatusApproved && (
                <Tooltip title={toggleLabel}>
                  <div className='flex flex-col items-center '>
                    <Switch checked={showChatHistory} onChange={handleToggleSwitch} size='medium' />
                    <span className='Type-Small-Regular text-center text-black'>{toggleLabel}</span>
                  </div>
                </Tooltip>
              )}
              {!isApproving && !isRejecting && (
                <TertiaryButton
                  type='button'
                  className='max-w-[350px] bg-[#F0F0F0] uppercase'
                  color='secondary'
                  variant='contained'
                  size='small'
                  onClick={handleDownloadLatestProof}
                >
                  {i18n._('Download')}
                </TertiaryButton>
              )}
              {!isApproving && !isRejecting && !isStatusApproved && (
                <DeclineButton
                  type='button'
                  color='success'
                  className='bg-[#D32F2F] uppercase'
                  disabled={status.toUpperCase() === 'DENIED'}
                  variant='contained'
                  size='small'
                  onClick={() => setIsRejecting(true)}
                >
                  {i18n._(/* i18n */ 'Decline')}
                </DeclineButton>
                // <Button
                //   color='white'
                //   className='Type-Base-Medium w-[25%] py-1'
                //   label={i18n._('Reject')}
                //   type='button'
                //   onClick={() => setIsRejecting(true)}
                //   iconName='thumb_down'
                //   iconClassName='text-xl'
                //   disabled={status.toUpperCase() === 'DENIED'}
                // />
              )}
              {!isApproving && !isRejecting && !isStatusApproved && (
                <ApproveButton
                  type='button'
                  color='success'
                  className='bg-[#2e7d32] uppercase'
                  disabled={status.toUpperCase() === 'DENIED'}
                  variant='contained'
                  size='small'
                  onClick={() => setIsApproving(true)}
                >
                  {i18n._(/* i18n */ 'Approve')}
                </ApproveButton>
              )}
              {isApproving && !isRejecting && !isStatusApproved && (
                // <Button
                //   color='gold'
                //   className='Type-Base-Medium w-[25%] py-1'
                //   label={i18n._('Submit Approval')}
                //   type='button'
                //   onClick={() => handleApproveProof(`${approverFirstName} ${approverLastName}`)}
                //   disabled={
                //     !approverFirstName ||
                //     approverFirstName.length <= 0 ||
                //     !approverLastName ||
                //     approverLastName.length <= 0
                //   }
                // />
                <ApproveButton
                  type='button'
                  color='success'
                  className='bg-[#2e7d32] uppercase'
                  disabled={
                    !approverFirstName ||
                    approverFirstName.length <= 0 ||
                    !approverLastName ||
                    approverLastName.length <= 0
                  }
                  variant='contained'
                  size='small'
                  onClick={() => handleApproveProof(`${approverFirstName} ${approverLastName}`)}
                >
                  {i18n._(/* i18n */ 'Submit Approval')}
                </ApproveButton>
              )}
              {!isApproving && isRejecting && !isStatusApproved && (
                // <Button
                //   color='gold'
                //   className='Type-Base-Medium w-[25%] py-1'
                //   label={i18n._('Submit Feedback')}
                //   type='button'
                //   onClick={() => handleDeclineProof(rejectComments)}
                //   disabled={!rejectComments || rejectComments.length <= 0}
                // />
                <DeclineButton
                  type='button'
                  color='success'
                  className='bg-[#D32F2F] uppercase'
                  disabled={!rejectComments || rejectComments.length <= 0}
                  variant='contained'
                  size='small'
                  onClick={() => handleDeclineProof(rejectComments)}
                >
                  {i18n._('Submit Feedback')}
                </DeclineButton>
              )}
              {(isApproving || isRejecting) && !isStatusApproved && (
                <TertiaryButton
                  type='button'
                  className='w-auto bg-[#F0F0F0] uppercase'
                  size='small'
                  onClick={() => {
                    setIsApproving(false)
                    setIsRejecting(false)
                  }}
                >
                  {i18n._('Back')}
                </TertiaryButton>
              )}
              {/* {!isApproving && !isRejecting && (
                <Button
                  color='white'
                  className='Type-Base-Medium w-[25%] py-1'
                  label={i18n._('Close')}
                  type='button'
                  onClick={() => {
                    resetValues()
                    setIsDisplayed(false)
                  }}
                />
              )} */}
              {!isMobile && <>{children}</>}
            </div>
          </div>
        </div>
        <div className='flex h-[330px] flex-col items-center justify-start border-b-2 border-20-grey bg-pure-white lg:h-[500px]'>
          {!isApproving && !isRejecting && (
            <div className='w-full overflow-auto'>
              {showChatHistory && <ChatBox proofs={proofs} />}
              {proofs && proofs.length > 0 && proofs[proofs.length - 1]?.file_url?.length > 0 && (
                <div className='border-2 border-t-black/40'>
                  {isPDFProofFile && isMobile && (
                    <div className='m-0 flex w-full items-center justify-center border-0 p-0'>
                      <Document
                        className='flex w-full items-center justify-center'
                        file={proofs[proofs.length - 1]?.file_url}
                      >
                        <Page className='w-full' pageNumber={1} />
                      </Document>
                    </div>
                  )}
                  {isPDFProofFile && !isMobile && (
                    <object
                      data={proofs[proofs.length - 1]?.file_url}
                      type='application/pdf'
                      width='100%'
                      height='auto'
                      aria-label='proof file'
                      className={`m-0 min-h-[398px] border-0 p-0 lg:min-h-[498px] ${
                        showChatHistory ? 'h-[800px]' : ''
                      }`}
                    />
                  )}
                  {!isPDFProofFile && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={proofs[proofs.length - 1]?.file_url}
                      width='100%'
                      height='100%'
                      alt='proof file'
                      className='m-0 min-h-[398px] w-[auto] border-0 p-0 lg:min-h-[498px]'
                    />
                  )}
                </div>
              )}
              {!proofs ||
                proofs.length <= 0 ||
                (proofs[proofs.length - 1]?.file_url?.length <= 0 && (
                  <p className='w-full pt-20 text-center italic'>
                    There was a problem loading the proof image.
                  </p>
                ))}
            </div>
          )}
          {!isApproving && isRejecting && !isStatusApproved && (
            <div className='mx-5 mt-10 flex flex-col items-center justify-center lg:mx-0 lg:mt-20'>
              <span className='Type-XL-Regular w-full text-center text-black lg:w-[80%]'>
                {i18n._('Please provide detailed feedback on the art adjustments needed.')}
              </span>
              <textarea
                className='Type-XL-Regular mt-10 w-full border border-solid lg:w-[70%]'
                placeholder='Type here...'
                rows={7}
                onChange={(e) => setRejectComments(e.target.value)}
                value={rejectComments}
              />
            </div>
          )}
          {isApproving && !isRejecting && !isStatusApproved && (
            <div className='mt-10 flex w-full flex-col items-center justify-center lg:mt-20'>
              <span className='Type-XXXL-Bold lg:w-[60%]'>Approved by: </span>
              <div className='mt-10 w-full gap-x-5 px-5 lg:grid lg:w-[60%] lg:grid-cols-[50fr_50fr] lg:px-0'>
                <TextInput
                  topLabel='First name'
                  required
                  error={!approverFirstName || approverFirstName.length <= 0}
                  className='mb-5 w-full text-black lg:mb-0'
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  onChange={(e) => setApproverFirstName((e.target as any).value)}
                  value={approverFirstName}
                />
                <TextInput
                  topLabel='Last name'
                  required
                  error={!approverLastName || approverLastName.length <= 0}
                  className='w-full text-black'
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  onChange={(e) => setApproverLastName((e.target as any).value)}
                  value={approverLastName}
                />
              </div>
            </div>
          )}
        </div>
        <div className='mx-5 my-5 flex flex-col gap-y-3 lg:hidden'>
          {!isApproving && !isRejecting && !isStatusApproved && (
            <ApproveButton
              type='button'
              color='success'
              className='w-full bg-[#2e7d32] uppercase'
              variant='contained'
              size='small'
              onClick={() => setIsApproving(true)}
            >
              {i18n._(/* i18n */ 'Approve')}
            </ApproveButton>
            // <Button
            //   color='gold'
            //   className='Type-Base-Medium w-full py-2'
            //   label={i18n._('Approve')}
            //   type='button'
            //   onClick={() => setIsApproving(true)}
            //   iconName='thumb_up'
            //   iconClassName='text-xl'
            // />
          )}
          {!isApproving && !isRejecting && !isStatusApproved && (
            <DeclineButton
              type='button'
              color='success'
              className='bg-[#D32F2F] uppercase'
              disabled={status.toUpperCase() === 'DENIED'}
              variant='contained'
              size='small'
              onClick={() => setIsRejecting(true)}
            >
              {i18n._(/* i18n */ 'Decline')}
            </DeclineButton>
          )}
          {isMobile && <>{children}</>}
          {!isApproving && !isRejecting && (
            <TertiaryButton
              type='button'
              className='w-full bg-[#F2F2F2] uppercase'
              color='secondary'
              variant='contained'
              size='small'
              onClick={handleDownloadLatestProof}
            >
              {i18n._('Download')}
            </TertiaryButton>
          )}
          {isApproving && !isRejecting && !isStatusApproved && (
            <ApproveButton
              type='button'
              color='success'
              className='w-full bg-[#2e7d32] uppercase'
              disabled={
                !approverFirstName ||
                approverFirstName.length <= 0 ||
                !approverLastName ||
                approverLastName.length <= 0
              }
              variant='contained'
              size='small'
              onClick={() => handleApproveProof(`${approverFirstName} ${approverLastName}`)}
            >
              {i18n._(/* i18n */ 'Submit Approval')}
            </ApproveButton>
          )}
          {!isApproving && isRejecting && !isStatusApproved && (
            <DeclineButton
              type='button'
              color='success'
              className='w-full bg-[#D32F2F] uppercase'
              disabled={!rejectComments || rejectComments.length <= 0}
              variant='contained'
              size='small'
              onClick={() => handleDeclineProof(rejectComments)}
            >
              {i18n._('Submit Feedback')}
            </DeclineButton>
          )}
          {(isApproving || isRejecting) && (
            <TertiaryButton
              type='button'
              className='w-full bg-[#F1F1F1] uppercase'
              size='small'
              onClick={() => {
                setIsApproving(false)
                setIsRejecting(false)
              }}
            >
              {i18n._('Back')}
            </TertiaryButton>
          )}
          {/* {!isApproving && !isRejecting && (
            <Button
              color='white'
              className='Type-Base-Medium w-full py-2'
              label={i18n._('Close')}
              type='button'
              onClick={() => {
                resetValues()
                setIsDisplayed(false)
              }}
            />
          )} */}
        </div>
        <ErrorPrompt error={error} />
        <AlertPrompt
          title={promptTitle}
          message={promptMessage}
          isDisplayed={displayPrompt}
          setIsDisplayed={setDisplayPrompt}
          callback={handleCloseAndResetValues}
        />
      </div>
    </Modal>
  )
}
